import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError } from '../../../lib/api/_request.js';
import type {
  SpecFindingResolutionInput,
  SpecReviewState,
  SpecReviewSubmitInput,
} from '../../../lib/api/spec-requirements.js';

/**
 * 理由が必要な解決と、そのボタンの文言・理由が無いときの案内。
 *
 * **見送りと取り消しを 1 つにしない。** 見送りは「指摘は正しいが、この案件では問題にならない」、
 * 取り消しは「指摘そのものが誤りだった」。記録を読む DLE がこの 2 つを区別できないと、
 * 本当の懸念だけを追えない。
 * @graph-connects none
 */
const NOTE_REQUIRED = [
  {
    action: 'dismissed',
    labelKey: 'spec.review.dismiss',
    missingKey: 'spec.review.dismissMissing',
  },
  {
    action: 'withdrawn',
    labelKey: 'spec.review.withdraw',
    missingKey: 'spec.review.withdrawMissing',
  },
] as const;

/**
 * 重さの見出し。**`critical` のような語をそのまま出さない。**
 *
 * 読む人が知りたいのは「放っておくとどうなるか」で、それは重さの定義そのもの
 * （critical＝このままでは作れない／major＝作ると手戻りが出る／minor＝読みやすさ）。
 * 記号を覚えさせるより、定義を出すほうが早い。
 * @graph-connects none
 */
const SEVERITY: Record<string, { labelKey: string; className: string }> = {
  critical: {
    labelKey: 'spec.review.severity.critical',
    className:
      'bg-[var(--color-error-solid)] text-[var(--color-error-text)] border-[var(--color-error)]',
  },
  major: {
    labelKey: 'spec.review.severity.major',
    className:
      'bg-[var(--color-warning-solid)] text-[var(--color-warning-text)] border-[var(--color-warning)]',
  },
  minor: {
    labelKey: 'spec.review.severity.minor',
    className:
      'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border-[var(--color-border)]',
  },
};

/**
 * 観点の見出しの翻訳キー。どの目で見て出た指摘かが分かると、直し方の見当が付く。
 * 鍵は API が返す観点の値そのもの（識別子なので訳さない）。
 * @graph-connects none
 */
const PERSPECTIVE_KEY: Record<string, string> = {
  pl: 'spec.review.perspective.pl',
  pdm: 'spec.review.perspective.pdm',
  impact: 'spec.review.perspective.impact',
  eng: 'spec.review.perspective.eng',
  density: 'spec.review.perspective.density',
};

/**
 * 差し戻しで出す指摘 1 件ぶんの入力行。
 *
 * **改行区切りの 1 つの欄にしない。** 指摘は 1 件ずつ id が付いて個別に解決されるので、
 * 改行が 1 つ混ざるだけで意図しない指摘が生まれる。行として持ち、行として見せる。
 * @graph-connects none
 */
interface SendBackRow {
  /** 行の識別子。行を消したときに残りの行の入力が隣へずれないように持つ */
  id: string;
  /** 指摘の本文 */
  summary: string;
}

/** 入力行 1 つぶんの初期値。 @graph-connects none */
function emptyRows(): SendBackRow[] {
  return [{ id: 'sb0', summary: '' }];
}

/**
 * 次に増やす行の識別子。残っている行の番号の次を使う。
 *
 * **採番を `useRef` で持たない。** DLE の節は差し戻しが通ると（状態が `ai_approved` から外れて）
 * いちど消えるので、ref だと消えるたびに 0 から振り直され、戻ってきたときに残っている行と
 * 同じ id を発行して React の key が重複する。行そのものから決めればこの経路が無くなる。
 * @graph-connects none
 */
function nextRowId(rows: SendBackRow[]): string {
  const used = rows.map((r) => Number.parseInt(r.id.slice(2), 10)).filter((n) => !Number.isNaN(n));
  return `sb${String(Math.max(-1, ...used) + 1)}`;
}

export interface ReviewActionsProps {
  /**
   * レビュー対象。業務フローは flow_id、案件に 1 つの成果物は narrative / features /
   * integration_test / validation_list / decision_log（domain の `SINGLETON_REVIEW_TARGETS`）
   */
  targetId: string;
  /** 対象の呼び名。同じ文言のボタンが節ごとに並ぶので、読み上げで区別するために使う */
  label: string;
  /** 今保存されている成果物の版 */
  version: number;
  /** レビュー状態。まだ依頼していなければ null */
  review: SpecReviewState | null;
  /** 操作中かどうか（連打を止める） */
  busy: boolean;
  /** 失敗したときの原因。権限・状態の前後関係はここに API のメッセージが入る */
  error: unknown;
  /** 今の版にレビューを固定して依頼する */
  onRequest: (targetId: string) => void;
  /** 指摘をまとめて解決する */
  onResolve: (targetId: string, resolutions: SpecFindingResolutionInput[]) => void;
  /** DLE のレビュー結果を出す */
  onSubmit: (targetId: string, input: SpecReviewSubmitInput) => void;
  /** DLE の承認を取り消して DLE レビュー待ちに戻す */
  onRevoke: (targetId: string) => void;
}

/**
 * 重さの呼び名。知らない値は API の返した値をそのまま出す（訳せない値を隠さない）。
 * @graph-connects none
 */
function severityLabel(t: TFunction, severity: string): string {
  const labelKey = SEVERITY[severity]?.labelKey;
  return labelKey === undefined ? severity : t(labelKey);
}

/**
 * 観点の呼び名。知らない値は API の返した値をそのまま出す（訳せない値を隠さない）。
 * @graph-connects none
 */
function perspectiveLabel(t: TFunction, perspective: string): string {
  const labelKey = PERSPECTIVE_KEY[perspective];
  return labelKey === undefined ? perspective : t(labelKey);
}

/**
 * 操作できなかった理由を人が読む形にする。API が返したメッセージはそのまま見せる
 * （入口で訳し直すと、API 側で文言が変わったときに黙ってずれる）。
 * @graph-connects none
 */
function reason(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return t('spec.review.error.forbidden');
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return t('spec.review.error.failed');
}

/** 承認ボタンの見た目。成功の色（緑）。差し戻しの警告色と取り違えないよう色で分ける。 @graph-connects none */
const APPROVE_CLASS =
  'text-sm px-4 py-2 rounded border border-[var(--color-success)] bg-[var(--color-success-solid)] text-[var(--color-success-text)] disabled:opacity-50 disabled:cursor-not-allowed';

/** 差し戻しボタンの見た目。警告の色（琥珀）。 @graph-connects none */
const SEND_BACK_CLASS =
  'text-sm px-4 py-2 rounded border border-[var(--color-warning)] bg-[var(--color-warning-solid)] text-[var(--color-warning-text)] disabled:opacity-50 disabled:cursor-not-allowed';

export interface ConfirmDialogProps {
  /** ダイアログの見出し */
  title: string;
  /** 見出しの下の説明 */
  body: string;
  /** 確定ボタンの文言 */
  confirmLabel: string;
  /** 確定ボタンの見た目 */
  confirmClassName: string;
  /** 見出しの下に出す補足（差し戻す指摘の一覧など） */
  children?: ReactNode;
  /** 確定したとき */
  onConfirm: () => void;
  /** やめたとき（Esc・背景クリックも含む） */
  onCancel: () => void;
}

/**
 * 取り返しの付きにくい操作の前に出す確認。
 *
 * **初期フォーカスは「キャンセル」に置く。** 連打・Enter の押し込みで確定側が押されないようにする。
 * 確定するまで呼び出し元の入力は消えない（キャンセルすれば書いた文章はそのまま残る）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business レビューの承認・差し戻しを確定する前に、押し間違いを止める確認を出す
 * @graph-connects none
 */
function ConfirmDialog({
  title,
  body,
  confirmLabel,
  confirmClassName,
  children,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onCancel]);

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-[var(--color-overlay)] p-4"
      role="presentation"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5 shadow-xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{title}</h2>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{body}</p>
        {children}
        <div className="mt-5 flex justify-end gap-3 flex-wrap">
          <button
            ref={cancelRef}
            type="button"
            className="text-sm px-4 py-2 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer"
            onClick={onCancel}
          >
            {t('spec.review.confirmCancel')}
          </button>
          <button
            type="button"
            className={`${confirmClassName} cursor-pointer`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export interface DleDecisionProps {
  /** 対象の呼び名（読み上げの区別用） */
  label: string;
  /** 操作中かどうか */
  busy: boolean;
  /** 差し戻す理由の入力行。状態は親（`ReviewActions`）が持つ */
  rows: SendBackRow[];
  /** 入力行を差し替える */
  onRowsChange: (next: SendBackRow[]) => void;
  /** 確認を通した DLE の結果を出す */
  onSubmit: (input: SpecReviewSubmitInput) => void;
}

/**
 * DLE の承認・差し戻し。
 *
 * - **差し戻しを上、承認を下に離す。** 以前は承認が差し戻しの直前に並び、承認だけが濃い色で、
 *   差し戻しの文章を書いている最中に承認を押す事故が起きていた。区切り線と余白で離し、
 *   色も意味で分ける（承認=緑、差し戻し=琥珀）。
 * - **どちらも確認を挟む。** 確定するまで入力欄は消さない。
 * - **書いた文章は差し戻しが通っても消さない。** このコンポーネントは状態が `ai_approved` の間だけ
 *   表示されるので、差し戻しが通ると（`dle_changes_requested` になって）節ごと消える。入力行を
 *   ここで持つと、そのとき書いた文章も一緒に失われ、指摘が解決されて再び DLE レビュー待ちに
 *   戻ってきたときに前のラウンドで何を指摘したかが画面に残らない。行の状態は消えない親
 *   （`ReviewActions`）に預ける。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE が承認または差し戻しを、確認を挟んで押し間違いなく出す
 * @graph-connects none
 */
function DleDecision({ label, busy, rows: sendBack, onRowsChange, onSubmit }: DleDecisionProps) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState<'approve' | 'sendBack' | null>(null);

  const addRow = () => {
    onRowsChange([...sendBack, { id: nextRowId(sendBack), summary: '' }]);
  };

  const editRow = (id: string, summary: string) => {
    onRowsChange(sendBack.map((r) => (r.id === id ? { ...r, summary } : r)));
  };

  const removeRow = (id: string) => {
    onRowsChange(sendBack.filter((r) => r.id !== id));
  };

  // 空の行は送らない。観点と重さは選ばせない。DLE の差し戻しは常にエンジニア観点で、
  // 塞ぐこと自体が目的なので major で固定する（値の妥当性は API が判定する）
  const sendBackFindings = sendBack
    .map((r) => r.summary.trim())
    .filter((s) => s !== '')
    .map((summary) => ({ perspective: 'eng', severity: 'major', summary }));

  const cancel = useCallback(() => {
    setConfirming(null);
  }, []);

  return (
    <div className="mt-4">
      <h4 className="text-xs font-semibold text-[var(--color-text-secondary)]">
        {t('spec.review.dleHeading')}
      </h4>
      <div className="mt-2 space-y-2">
        {sendBack.map((row, i) => (
          <div key={row.id} className="flex gap-2">
            <input
              className="flex-1 border border-[var(--color-border)] rounded px-3 py-2 text-sm"
              placeholder={t('spec.review.sendBackPlaceholder')}
              aria-label={t('spec.review.sendBackRowAria', { index: i + 1 })}
              value={row.summary}
              onChange={(e) => {
                editRow(row.id, e.target.value);
              }}
            />
            {sendBack.length > 1 ? (
              <button
                type="button"
                disabled={busy}
                className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                aria-label={t('spec.review.sendBackRowRemoveAria', { index: i + 1 })}
                onClick={() => {
                  removeRow(row.id);
                }}
              >
                {t('spec.action.remove')}
              </button>
            ) : null}
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2 flex-wrap items-center">
        <button
          type="button"
          disabled={busy}
          className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          aria-label={t('spec.review.addFindingAria')}
          onClick={addRow}
        >
          {t('spec.review.addFinding')}
        </button>
        <button
          type="button"
          disabled={busy || sendBackFindings.length === 0}
          className={`${SEND_BACK_CLASS} cursor-pointer`}
          aria-label={t('spec.review.sendBackAria', { label })}
          onClick={() => {
            setConfirming('sendBack');
          }}
        >
          {sendBackFindings.length === 0
            ? t('spec.review.sendBack')
            : t('spec.review.sendBackCount', { n: sendBackFindings.length })}
        </button>
      </div>

      <div className="mt-6 border-t border-[var(--color-border)] pt-4">
        <button
          type="button"
          disabled={busy}
          className={`${APPROVE_CLASS} cursor-pointer`}
          aria-label={t('spec.review.approveAria', { label })}
          onClick={() => {
            setConfirming('approve');
          }}
        >
          {t('spec.review.approve')}
        </button>
      </div>

      {confirming === 'approve' ? (
        <ConfirmDialog
          title={t('spec.review.approveConfirmTitle', { label })}
          body={t('spec.review.approveConfirmBody')}
          confirmLabel={t('spec.review.approve')}
          confirmClassName={APPROVE_CLASS}
          onCancel={cancel}
          onConfirm={() => {
            setConfirming(null);
            onSubmit({ reviewer: 'dle', findings: [] });
          }}
        />
      ) : null}
      {confirming === 'sendBack' ? (
        <ConfirmDialog
          title={t('spec.review.sendBackConfirmTitle', { label })}
          body={t('spec.review.sendBackConfirmBody', { n: sendBackFindings.length })}
          confirmLabel={t('spec.review.sendBack')}
          confirmClassName={SEND_BACK_CLASS}
          onCancel={cancel}
          onConfirm={() => {
            setConfirming(null);
            onSubmit({ reviewer: 'dle', findings: sendBackFindings });
          }}
        >
          <ul className="mt-3 list-disc pl-5 text-sm space-y-1">
            {sendBackFindings.map((f, i) => (
              <li key={`${String(i)}:${f.summary}`}>{f.summary}</li>
            ))}
          </ul>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/**
 * レビュー対象(業務フロー・ナラティブ・機能一覧・バリデーション一覧・総合テスト・決定記録)1 件への
 * レビュー操作。
 *
 * 状態遷移の判定は API が持つ（AI レビューが通る前の DLE 提出は拒否される等）。画面は状態に
 * 応じて押せるものを出すだけで、可否は API の応答に従う。
 *
 * 差し戻しは入力行を並べて複数件を 1 回で出す。API・MCP は元から `findings` を配列で受けており、
 * 画面だけが 1 件に絞っていた非対称を埋めるため。その入力行は（差し戻しが通ると消える
 * `DleDecision` ではなく）状態に関わらず残るここで持つので、書いた文章は差し戻しの前後で消えない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business レビュー対象(業務フロー・ナラティブ・機能一覧・バリデーション一覧・総合テスト・決定記録)の
 *   レビュー依頼・指摘の解決・DLE の承認と複数件をまとめた差し戻しを画面から行う
 * @graph-connects ApiError [references] 操作が失敗した理由の判別
 */
export function ReviewActions({
  targetId,
  label,
  version,
  review,
  busy,
  error,
  onRequest,
  onResolve,
  onSubmit,
  onRevoke,
}: ReviewActionsProps) {
  const { t } = useTranslation();
  const stale = review !== null && review.targetVersion !== version;
  const [notes, setNotes] = useState<Record<string, string>>({});
  // DLE の差し戻す理由。`DleDecision` は差し戻しが通ると消えるので、行はここで持つ
  // （消える側に置くと、通った時点で書いた文章も失われる）
  const [sendBackRows, setSendBackRows] = useState<SendBackRow[]>(emptyRows);

  const resolve = (findingId: string, resolution: SpecFindingResolutionInput['resolution']) => {
    onResolve(targetId, [{ findingId, resolution, note: notes[findingId] ?? '' }]);
  };

  return (
    <div className="mt-4 border-t border-[var(--color-border)] pt-3">
      {review === null || stale ? (
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            disabled={busy}
            className="text-sm px-4 py-2 rounded bg-[var(--color-accent-primary)] text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
            aria-label={t(
              review === null ? 'spec.review.requestAria' : 'spec.review.requestAgainAria',
              { label },
            )}
            onClick={() => {
              onRequest(targetId);
            }}
          >
            {t(review === null ? 'spec.review.request' : 'spec.review.requestAgain')}
          </button>
          {stale ? (
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('spec.review.stale', { version: review.targetVersion })}
            </span>
          ) : null}
        </div>
      ) : null}

      {review !== null && review.openFindings.length > 0 ? (
        <div className={review === null || stale ? 'mt-4' : ''}>
          <h4 className="text-xs font-semibold text-[var(--color-text-secondary)]">
            {t('spec.review.findingsHeading', { n: review.openFindings.length })}
          </h4>
          <ul className="mt-2 space-y-3">
            {review.openFindings.map((f) => (
              <li key={f.id} className="border border-[var(--color-border)] rounded p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border ${
                      SEVERITY[f.severity]?.className ??
                      'bg-[var(--color-bg-elevated)] border-[var(--color-border)]'
                    }`}
                  >
                    {severityLabel(t, f.severity)}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {perspectiveLabel(t, f.perspective)}
                    {f.target === '' ? '' : ` ・ ${f.target}`}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)] ml-auto font-mono">
                    {f.id}
                  </span>
                </div>
                <p className="mt-2 text-sm">{f.summary}</p>
                <input
                  className="mt-2 block w-full border border-[var(--color-border)] rounded px-3 py-2 text-sm"
                  placeholder={t('spec.review.notePlaceholder')}
                  aria-label={t('spec.review.noteAria', { id: f.id })}
                  value={notes[f.id] ?? ''}
                  onChange={(e) => {
                    setNotes((n) => ({ ...n, [f.id]: e.target.value }));
                  }}
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                    onClick={() => {
                      resolve(f.id, 'fixed');
                    }}
                  >
                    {t('spec.review.fixed')}
                  </button>
                  {NOTE_REQUIRED.map(({ action, labelKey, missingKey }) => (
                    <button
                      key={action}
                      type="button"
                      disabled={busy || (notes[f.id] ?? '').trim() === ''}
                      className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                      title={(notes[f.id] ?? '').trim() === '' ? t(missingKey) : undefined}
                      onClick={() => {
                        resolve(f.id, action);
                      }}
                    >
                      {t(labelKey)}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {review !== null && !stale && review.status === 'ai_approved' ? (
        <DleDecision
          label={label}
          busy={busy}
          rows={sendBackRows}
          onRowsChange={setSendBackRows}
          onSubmit={(input) => {
            onSubmit(targetId, input);
          }}
        />
      ) : null}

      {review !== null && !stale && review.status === 'requested' ? (
        <p role="status" className="mt-4 text-sm text-[var(--color-text-secondary)]">
          {t('spec.review.aiReviewing')}
        </p>
      ) : null}

      {review !== null && !stale && review.status === 'approved' ? (
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-[var(--color-success-text)]">
            {t('spec.review.approvedNote')}
          </span>
          <button
            type="button"
            disabled={busy}
            className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
            aria-label={t('spec.review.revokeAria', { label })}
            onClick={() => {
              onRevoke(targetId);
            }}
          >
            {t('spec.review.revoke')}
          </button>
          <span className="text-xs text-[var(--color-text-muted)]">
            {t('spec.review.revokeHint')}
          </span>
        </div>
      ) : null}

      {error === null || error === undefined ? null : (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error)] whitespace-pre-wrap">
          {reason(t, error)}
        </p>
      )}
    </div>
  );
}
